// Register Now button click tracking utility - Now uses server-side API
const API_BASE = process.env.REACT_APP_API_BASE || '/api';
const STORAGE_KEY = 'register_click_analytics'; // Fallback to localStorage

// Track Register Now button click - sends to server
export const trackRegisterClick = async (page) => {
  try {
    // Try server-side tracking first
    const response = await fetch(`${API_BASE}/register/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ page }),
    });

    if (response.ok) {
      const result = await response.json();
      return result.tracked ? result.data : null;
    }
    
    // Fallback to localStorage if server fails
    throw new Error('Server tracking failed, using localStorage fallback');
  } catch (error) {
    // Fallback to localStorage
    try {
      const clickData = {
        page: page,
        timestamp: new Date().toISOString(),
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString()
      };

      // Get existing data from localStorage
      const existingData = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      
      // Add new click
      existingData.push(clickData);
      
      // Keep only last 1000 clicks to prevent storage issues
      const recentClicks = existingData.slice(-1000);
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(recentClicks));
      
      return clickData;
    } catch (localError) {
      console.error('Error tracking register click:', localError);
      return null;
    }
  }
};

// Cache for register click data to reduce API calls
let registerClickCache = null;
let registerClickCacheTimestamp = null;
const CACHE_DURATION = 2000; // 2 seconds cache

// Get all register click data from server
export const getRegisterClickData = async () => {
  try {
    // Use cache if available and fresh
    const now = Date.now();
    if (registerClickCache && registerClickCacheTimestamp && (now - registerClickCacheTimestamp) < CACHE_DURATION) {
      return registerClickCache.allClicks || [];
    }

    const response = await fetch(`${API_BASE}/register/analytics`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch register click data');
    }

    const result = await response.json();
    
    if (result.ok && result.data) {
      registerClickCache = result.data;
      registerClickCacheTimestamp = now;
      return result.data.allClicks || [];
    }
    
    // Fallback to localStorage
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch (error) {
    console.error('Error getting register click data:', error);
    // Fallback to localStorage
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (localError) {
      return [];
    }
  }
};

// Get total register clicks count (async)
export const getTotalRegisterClicks = async () => {
  try {
    const now = Date.now();
    if (registerClickCache && registerClickCacheTimestamp && (now - registerClickCacheTimestamp) < CACHE_DURATION) {
      return registerClickCache.totalClicks || 0;
    }

    const response = await fetch(`${API_BASE}/register/analytics`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch register click analytics');
    }

    const result = await response.json();
    
    if (result.ok && result.data) {
      registerClickCache = result.data;
      registerClickCacheTimestamp = now;
      return result.data.totalClicks || 0;
    }
    
    // Fallback to localStorage
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return data.length;
  } catch (error) {
    console.error('Error getting total register clicks:', error);
    // Fallback to localStorage
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return data.length;
    } catch (localError) {
      return 0;
    }
  }
};

// Get register clicks by page (async)
export const getRegisterClicksByPage = async () => {
  try {
    const now = Date.now();
    if (registerClickCache && registerClickCacheTimestamp && (now - registerClickCacheTimestamp) < CACHE_DURATION) {
      return registerClickCache.clicksByPage || [];
    }

    const response = await fetch(`${API_BASE}/register/analytics`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch register click analytics');
    }

    const result = await response.json();
    
    if (result.ok && result.data) {
      registerClickCache = result.data;
      registerClickCacheTimestamp = now;
      return result.data.clicksByPage || [];
    }
    
    // Fallback to localStorage
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const pageCount = {};
    data.forEach(click => {
      pageCount[click.page] = (pageCount[click.page] || 0) + 1;
    });
    return Object.entries(pageCount)
      .map(([page, count]) => ({ page, count }))
      .sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error getting register clicks by page:', error);
    // Fallback to localStorage
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const pageCount = {};
      data.forEach(click => {
        pageCount[click.page] = (pageCount[click.page] || 0) + 1;
      });
      return Object.entries(pageCount)
        .map(([page, count]) => ({ page, count }))
        .sort((a, b) => b.count - a.count);
    } catch (localError) {
      return [];
    }
  }
};

// Get register clicks for specific pages (home, about, summit-objective) (async)
export const getRegisterClicksForTrackedPages = async () => {
  try {
    const now = Date.now();
    if (registerClickCache && registerClickCacheTimestamp && (now - registerClickCacheTimestamp) < CACHE_DURATION) {
      return registerClickCache.clicksForTrackedPages || [];
    }

    const response = await fetch(`${API_BASE}/register/analytics`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch register click analytics');
    }

    const result = await response.json();
    
    if (result.ok && result.data) {
      registerClickCache = result.data;
      registerClickCacheTimestamp = now;
      return result.data.clicksForTrackedPages || [];
    }
    
    // Fallback to localStorage
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const trackedPages = ['home', 'about', 'summit-objective'];
    const pageCount = {};
    trackedPages.forEach(page => {
      pageCount[page] = 0;
    });
    data.forEach(click => {
      if (trackedPages.includes(click.page)) {
        pageCount[click.page] = (pageCount[click.page] || 0) + 1;
      }
    });
    return Object.entries(pageCount)
      .map(([page, count]) => ({ page, count }))
      .sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error getting register clicks for tracked pages:', error);
    // Fallback to localStorage
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const trackedPages = ['home', 'about', 'summit-objective'];
      const pageCount = {};
      trackedPages.forEach(page => {
        pageCount[page] = 0;
      });
      data.forEach(click => {
        if (trackedPages.includes(click.page)) {
          pageCount[click.page] = (pageCount[click.page] || 0) + 1;
        }
      });
      return Object.entries(pageCount)
        .map(([page, count]) => ({ page, count }))
        .sort((a, b) => b.count - a.count);
    } catch (localError) {
      return [];
    }
  }
};

// Get register clicks by date (async)
export const getRegisterClicksByDate = async () => {
  try {
    const now = Date.now();
    if (registerClickCache && registerClickCacheTimestamp && (now - registerClickCacheTimestamp) < CACHE_DURATION) {
      return registerClickCache.clicksByDate || {};
    }

    const response = await fetch(`${API_BASE}/register/analytics`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch register click analytics');
    }

    const result = await response.json();
    
    if (result.ok && result.data) {
      registerClickCache = result.data;
      registerClickCacheTimestamp = now;
      return result.data.clicksByDate || {};
    }
    
    // Fallback to localStorage
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const dateGroups = {};
    data.forEach(click => {
      const date = click.date || new Date(click.timestamp).toLocaleDateString();
      dateGroups[date] = (dateGroups[date] || 0) + 1;
    });
    return dateGroups;
  } catch (error) {
    console.error('Error getting register clicks by date:', error);
    // Fallback to localStorage
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const dateGroups = {};
      data.forEach(click => {
        const date = click.date || new Date(click.timestamp).toLocaleDateString();
        dateGroups[date] = (dateGroups[date] || 0) + 1;
      });
      return dateGroups;
    } catch (localError) {
      return {};
    }
  }
};

// Clear register click cache (force refresh)
export const clearRegisterClickCache = () => {
  registerClickCache = null;
  registerClickCacheTimestamp = null;
};

// Clear all register click data (server-side)
export const clearRegisterClickData = async () => {
  try {
    // Note: This would require a DELETE endpoint on the server
    // For now, we'll just clear the cache and localStorage
    clearRegisterClickCache();
    localStorage.removeItem(STORAGE_KEY);
    console.warn('clearRegisterClickData: Server-side clear not implemented. Cache and localStorage cleared only.');
  } catch (error) {
    console.error('Error clearing register click data:', error);
  }
};

